import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_NUMBER_PATTERNS, NUMBER_PATTERN_TOKENS, SEQUENCE_START, renderNumberPattern } from '@his/shared';
import { put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import { NUMBERING_SERIES, patternProblemKey, patternProblemText, resetKey, seriesHint, seriesName, seriesState, tokenMeaning } from './number-series';

/**
 * How every number the place issues is shaped (SRS 0.3).
 *
 * The preview renders exactly what the server will: same tokens, same
 * check-digit arithmetic. What cannot stay unique — a monthly counter with no
 * month in it — is refused here and again on the server.
 */
export function NumberingTab({
  readOnly,
  patterns,
  timezone,
  modules,
  retiredModules = [],
}: {
  readOnly: boolean;
  patterns: Record<string, string>;
  timezone?: string;
  modules: readonly string[];
  retiredModules?: readonly string[];
}) {
  const SERIES = NUMBERING_SERIES.filter((type) => seriesState(type, modules, retiredModules) !== 'hidden');
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(SERIES.map((type) => [type, patterns[type] ?? DEFAULT_NUMBER_PATTERNS[type] ?? '{SEQ:6}'])),
  );

  const changed = SERIES.filter((type) => draft[type] !== (patterns[type] ?? DEFAULT_NUMBER_PATTERNS[type]));
  const problems = Object.fromEntries(SERIES.map((type) => [type, patternProblemText(type, draft[type] ?? '')]));
  const invalid = changed.some((type) => problems[type]);

  const save = useMutation({
    mutationFn: () =>
      put('/org/settings', {
        numberPatterns: Object.fromEntries(changed.map((type) => [type, draft[type]])),
      }),
    onSuccess: () => {
      toast.success(tr('admin', 'numbering_saved'), tr('admin', 'the_next_number_of_each_changed_series_uses_the'));
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
    onError: (error) => toast.error(tr('admin', 'could_not_save_the_numbering'), describeError(error)),
  });

  const preview = (type: (typeof SERIES)[number]) => {
    const pattern = draft[type] ?? '';
    if (patternProblemKey(type, pattern)) return '—';
    return renderNumberPattern(pattern, {
      branchCode: 'MAIN',
      date: new Date(),
      seq: SEQUENCE_START[type] ?? 1,
      timeZone: timezone,
    });
  };

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-sm">{tr('admin', 'number_series')}</CardTitle>
          {!readOnly && (
            <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={changed.length === 0 || invalid}>
              {changed.length > 0 ? tr('admin', 'save_x_changes', { count: changed.length }) : tr('admin', 'save')}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {readOnly && <Alert>{tr('admin', 'you_can_view_the_numbering_but_not_change_it')}</Alert>}
          <Alert>
            {tr('admin', 'changing_a_pattern_affects_the')} <span className="font-medium">{tr('admin', 'next')}</span> {tr('admin', 'number_only_nothing_already_issued_is')}
          </Alert>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-start text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="py-1.5 pe-3">{tr('admin', 'series')}</th>
                  <th className="py-1.5 pe-3">{tr('admin', 'pattern')}</th>
                  <th className="py-1.5 pe-3">{tr('admin', 'example')}</th>
                </tr>
              </thead>
              <tbody>
                {SERIES.map((type) => {
                  const problem = problems[type];
                  const dirty = draft[type] !== (patterns[type] ?? DEFAULT_NUMBER_PATTERNS[type]);
                  const archived = seriesState(type, modules, retiredModules) === 'archived';
                  const hint = archived ? tr('admin', 'archived_module_read_only') : seriesHint(type);
                  return (
                    <tr key={type} className="border-t align-top">
                      <td className="py-2 pe-3">
                        <p className="font-medium">{seriesName(type)}</p>
                        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
                      </td>
                      <td className="py-2 pe-3">
                        <Input
                          className={cn('h-8 w-56 font-mono text-xs', problem && 'border-destructive')}
                          value={draft[type] ?? ''}
                          dir="ltr"
                          aria-label={seriesName(type)}
                          disabled={readOnly || archived}
                          onChange={(e) => setDraft({ ...draft, [type]: e.target.value })}
                        />
                        {problem && <p className="mt-1 text-xs text-destructive">{problem}</p>}
                        {!problem && dirty && (
                          <button
                            type="button"
                            className="mt-1 text-xs text-muted-foreground underline-offset-4 hover:underline"
                            onClick={() => setDraft({ ...draft, [type]: patterns[type] ?? DEFAULT_NUMBER_PATTERNS[type] ?? '{SEQ:6}' })}
                          >
                            {tr('admin', 'undo')}
                          </button>
                        )}
                      </td>
                      <td className="py-2 pe-3 font-mono text-xs">
                        <span dir="ltr">{preview(type)}</span>
                        <span className="block font-sans text-[11px] text-muted-foreground">{tr('admin', resetKey(type))}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('admin', 'tokens')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {NUMBER_PATTERN_TOKENS.map((token) => (
            <div key={token.token}>
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{token.token}</code>
              <p className="text-xs text-muted-foreground">{tokenMeaning(token.token)}</p>
            </div>
          ))}
          <p className="pt-2 text-xs text-muted-foreground">
            {tr('admin', 'anything_outside_braces_is_printed_as_is')} <code className="font-mono">P-{'{SEQ:6}'}{'{CHECK}'}</code> {tr('admin', 'gives')}{' '}
            <code className="font-mono">P-1000015</code>{tr('admin', 'a_receptionist_who_mistypes_a_digit_is_told_so')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
